import {createSelector} from 'reselect';
import {List} from 'immutable';
import type {
  Locale,
  LostTranslationRecord,
  LocalizationState,
} from '@packages/systems/localization-types';
import type {NativeId} from '@packages/systems/core/utils/NativeIdUtils';
import {
  selectSelectedElement,
  selectCurrentComponent,
  selectSelectedNativeIdPath,
  selectFocusedSymbolNativeIdPath,
  selectComponents,
} from '@packages/utilities/platform-selectors';
import {memoize, singleMemo} from '@packages/utilities/memo';
import {isElementLocalized} from './isElementLocalized';
import {isSymbolLocalized} from './isSymbolLocalized';
import {qualify} from '@packages/systems/wfdl-Qualified';
import {Component, Expressions, Plugins} from '@packages/systems/wfdl';
import type {DesignerStateType} from '@packages/systems/core/records/DesignerState';
import type {UiNodeStateType} from '@packages/systems/core/records/UiNodeState';
import type {ItemRecordType} from '@packages/systems/core/records/ItemRecord';
import {getAllAssociatedItemsInLocale} from './Locale';
import {
  extractElementPropertiesTranslatableData,
  isComponentPropertyDefaultValueLocalized,
} from './getTranslatableDataFromElement';
import type {Entitlement} from '@packages/domains/billing/types';
import type {
  DesignerStores,
  SharedStores,
} from '@packages/systems/designer/flux';
import {isCommerceCollection} from '@packages/systems/commerce/utils/CommerceCollectionUtils';
import {
  getInnermostSymbolInstance,
  getRelativePath,
  isComponentRoot,
  getElementFromNodeInInstanceNativeId,
  findAllDescendantsFromExpression,
} from '@packages/utilities/platform';
import {isElementReferenceBoundToProperty} from '@packages/utilities/elementTypeUtils';
import {getTotalNumberOfItems} from '@packages/systems/dynamo/utils/CollectionUtils';
import {emptyArray} from '@packages/utilities/fp/utils';
import {force} from '@packages/utilities/lazy';
import type {CollectionStateType} from '@packages/systems/core/records/CollectionStateType';
import type {CurrentCollectionStateType} from '@packages/systems/cms/types';
import {getIsPanelActive} from '@packages/systems/panels';
import {isElementStyleLocalized} from './isElementStyleLocalized';

export const selectIsOnPrimaryLocale = (state: LocalizationState): boolean => {
  /**
   * If there's no current locale, we can assume that the site doesn't have Localization set up, which means
   * we want to treat this selector to be on the "primary" locale.
   */
  if (!state?.currentLocale) {
    return true;
  }

  return state.currentLocale._id === state.primaryLocale?._id;
};

export const selectIsOnSecondaryLocale = (
  state: LocalizationState
): boolean => {
  if (!state?.currentLocale) {
    return false;
  }

  return !selectIsOnPrimaryLocale(state);
};

export const selectCurrentLocale = (
  state: LocalizationState
): Locale | null | undefined => {
  return state.currentLocale;
};

export const selectPrimaryLocale = (
  state: LocalizationState
): Locale | null | undefined => {
  if (!state) {
    return undefined;
  }

  return state.primaryLocale;
};

export const selectHasSecondaryLocale = (state: LocalizationState) =>
  state.locales.length > 0;

export const selectSecondaryLocales = (state: LocalizationState) =>
  state.locales;

export const selectEnabledSecondaryLocales: (
  state: LocalizationState
) => Locale[] = createSelector(
  (state: LocalizationState) => state.locales,
  (locales): Locale[] => locales.filter((locale) => locale.enabled)
);

export const selectAllLocales: (state: LocalizationState) => Locale[] =
  createSelector(
    (state: LocalizationState) => state?.primaryLocale,
    (state: LocalizationState) => state?.locales,
    (primaryLocale, locales): Locale[] => {
      if (!primaryLocale?._id) {
        return locales;
      }

      // Locales don't have the redirect option so we can strip it out
      const {redirect: _, ...filteredPrimaryLocale} = primaryLocale;
      filteredPrimaryLocale.primary = true;

      return [filteredPrimaryLocale, ...(locales || [])];
    }
  );

export const selectPropagatingSecondaryLocales: (
  state: LocalizationState
) => Locale[] = createSelector(
  (state: LocalizationState) => state.locales,
  (locales): Locale[] =>
    locales.filter((locale) => !locale.preventPrimaryCmsItemPropagation)
);

export const selectMaxLocalesEntitlement = (
  state: DesignerStores
): Entitlement | undefined => state.EntitlementsStore?.entitlements?.maxLocales;

export const selectMaxLocalesFromOldSitePlan = (
  state: DesignerStores
): number => state.SiteDataStore?.maxLocales ?? 0;

export const selectEffectiveMaxLocales = createSelector(
  selectMaxLocalesEntitlement,
  selectMaxLocalesFromOldSitePlan,
  (maxLocalesEntitlement, maxLocalesFromOldSitePlan) =>
    Math.max(
      maxLocalesFromOldSitePlan,
      maxLocalesEntitlement?.entitlementLimit ?? 0
    )
);

export const selectCanAddLocale = createSelector(
  selectEffectiveMaxLocales,
  (state: DesignerStores) => state.LocalizationStore.staged,
  (effectiveMaxLocales, staged) => {
    // The staged locales always includes the primary, but for the entitlement checking, we only care about the secondaries
    const currentNumberOfLocales = staged.locales.size - 1;

    return currentNumberOfLocales < effectiveMaxLocales;
  }
);

export const selectCanEnableOneMoreSecondaryLocale = createSelector(
  selectEffectiveMaxLocales,
  (state: DesignerStores) => state.LocalizationStore.staged,
  (effectiveMaxLocales, staged) => {
    const enabledSecondaryLocales = staged.locales.filter((l) =>
      Boolean(!l?.primary && l?.enabled)
    );
    return enabledSecondaryLocales.size < effectiveMaxLocales;
  }
);

export const selectPrimaryDom = (
  state: LocalizationState
): LocalizationState['dom']['primary'] => {
  return state.dom.primary;
};

export const selectLocalizedDom = (
  state: LocalizationState
): LocalizationState['dom']['localized'] => {
  return state.dom.localized;
};

export const selectLocalizedSymbols = (
  state: LocalizationState
): LocalizationState['symbols']['localized'] => {
  return state.symbols.localized;
};

export const selectIsSelectedElementLocalized = (state: {
  LocalizationStore: LocalizationState;
  DesignerStore: DesignerStateType;
  UiNodeStore: UiNodeStateType;
  StyleBlockStore: DesignerStores['StyleBlockStore'];
}): boolean => {
  // Translations are only available on secondary locales.
  if (selectIsOnPrimaryLocale(state.LocalizationStore)) {
    return false;
  }

  const selectedElement = selectSelectedElement(state);

  if (!selectedElement) return false;

  if (isElementStyleLocalized(selectedElement, state)) {
    return true;
  }

  const selectedNativeIdPath = selectSelectedNativeIdPath(state);
  const componentMap = selectComponents(state);

  if (isComponentRoot(componentMap)(selectedElement)) {
    const focusedSymbolNativeIdPath = selectFocusedSymbolNativeIdPath(state);

    // If this is true it means the user is actually selecting the component instance (so the component root from outside).
    const isSelectingWithinSymInstance =
      getRelativePath(selectedNativeIdPath, focusedSymbolNativeIdPath).length >
      1;

    if (isSelectingWithinSymInstance) {
      // If the selected element is a component instance we need to
      // look for it in the localized dom and localized symbols maps.
      const componentInstance = getInnermostSymbolInstance(
        selectedNativeIdPath,
        componentMap
      );

      if (componentInstance) {
        const localizedDomNodes = selectLocalizedDom(state.LocalizationStore);
        const localizedComponentNodes = selectLocalizedSymbols(
          state.LocalizationStore
        );

        if (
          localizedDomNodes[componentInstance.id] ||
          localizedComponentNodes[componentInstance.id]
        ) {
          return true;
        }
      }
    }

    const componentType = qualify(
      Plugins.name('__SitePlugin'),
      Component.name(selectedElement.id)
    );

    return isSymbolLocalized(
      componentType,
      selectLocalizedSymbols(state.LocalizationStore),
      componentMap
    );
  }

  if (isElementReferenceBoundToProperty(selectedElement)) {
    const selectedElementPropertiesTranslatableData =
      extractElementPropertiesTranslatableData(componentMap)(selectedElement)(
        selectedNativeIdPath
      );

    if (selectedElementPropertiesTranslatableData.length > 0) {
      const localizedDomNodes = selectLocalizedDom(state.LocalizationStore);
      const localizedComponentNodes = selectLocalizedSymbols(
        state.LocalizationStore
      );
      const primaryComponents = selectPrimaryDom(state.LocalizationStore);

      const hasSomePropertyLocalized =
        selectedElementPropertiesTranslatableData.some((translatableProp) => {
          if (translatableProp.type === 'component-property') {
            // If it's a property default value.
            return isComponentPropertyDefaultValueLocalized(
              translatableProp,
              force(primaryComponents)
            );
          } else {
            // If it's a property override node.
            if (
              localizedDomNodes[translatableProp._id] ||
              localizedComponentNodes[translatableProp._id]
            ) {
              return true;
            }

            if (
              translatableProp.type === 'whole-text' &&
              translatableProp.fieldId
            ) {
              const componentInstance = getInnermostSymbolInstance(
                selectedNativeIdPath,
                componentMap
              );
              if (componentInstance) {
                const overrideValues = Expressions.getIn(
                  Expressions.getElementData(componentInstance),
                  [{in: 'Record', at: translatableProp.fieldId}]
                );
                if (overrideValues) {
                  const typographicElement = Expressions.getElementById(
                    overrideValues,
                    translatableProp._id
                  );
                  if (typographicElement) {
                    const hasLegacyMatch = findAllDescendantsFromExpression(
                      Expressions.getElementData(typographicElement),
                      () => true
                    ).some((descendant) => {
                      const id = Expressions.getElementId(descendant);
                      return (
                        Boolean(localizedDomNodes[id]) ||
                        Boolean(localizedComponentNodes[id])
                      );
                    });
                    if (hasLegacyMatch) {
                      return true;
                    }
                  }
                }
              }
            }
            return false;
          }
        });

      // The element can have its static text localized but a property bound to an attr not localized,
      // so at this point only consider the element localized if at least one property is localized.
      if (hasSomePropertyLocalized) {
        return true;
      }
    }
  }

  const currentComponent = selectCurrentComponent(state);

  if (
    isElementLocalized(
      selectedElement,
      selectLocalizedDom(state.LocalizationStore),
      currentComponent
    )
  ) {
    return true;
  }

  // If it was not a localized component neither a node, try if it is a node inside a component.
  return isElementLocalized(
    selectedElement,
    selectLocalizedSymbols(state.LocalizationStore),
    currentComponent
  );
};

export const selectLocaleById = memoize((locales: Locale[]) =>
  singleMemo((localeId: string): Locale | null | undefined => {
    return locales.find((locale) => String(locale._id) === String(localeId));
  })
);

export const selectLocaleByCmsId = memoize((locales: Locale[]) =>
  singleMemo((cmsLocaleId: string): Locale | null | undefined => {
    return locales.find(
      (locale) => String(locale.cmsId) === String(cmsLocaleId)
    );
  })
);

export const selectAllAssociatedItemsInLocaleFromCurrentItem = createSelector(
  (state: DesignerStores) => state.CurrentCollectionStore.currentItem,
  (state: DesignerStores) => state.CurrentCollectionStore.currentCollection,
  (state: DesignerStores) => state.CollectionStore,
  (currentItem, currentCollection, collectionState): List<ItemRecordType> => {
    if (!currentItem || !currentCollection) {
      return List();
    }

    const items = collectionState.itemsFor(currentCollection, {
      allLocales: true,
    });

    return getAllAssociatedItemsInLocale(currentItem, items, false);
  }
);

export const selectAssociatedPrimaryItemFromCurrentItem = createSelector(
  (state: DesignerStores) =>
    state.CurrentCollectionStore.currentItem?.get('_id'),
  (state: DesignerStores) => state.CurrentCollectionStore.currentCollection,
  (state: DesignerStores) => selectPrimaryLocale(state.LocalizationStore),
  (state: DesignerStores) => state.CollectionStore,
  (currentItemId, currentCollection, primaryLocale, collectionState) => {
    if (!currentItemId || !currentCollection || !primaryLocale) {
      return null;
    }

    const items = collectionState.itemsFor(currentCollection, {
      allLocales: true,
    });

    return items.find(
      (item) =>
        item?.get('_id') === currentItemId &&
        String(item?.get('_locale')) === String(primaryLocale.cmsId)
    );
  }
);

export const selectMaybeLocalizedItemCountByCollectionIdByCurrentLocale =
  createSelector(
    (state: {LocalizationStore: LocalizationState}) =>
      selectCurrentLocale(state.LocalizationStore),
    (state: {LocalizationStore: LocalizationState}) =>
      selectPrimaryLocale(state.LocalizationStore),
    (state: {CollectionStore: CollectionStateType}) =>
      state.CollectionStore.localizedItemCountByLocaleId,
    (state: {CollectionStore: CollectionStateType}) =>
      state.CollectionStore.__DEPRECATED__itemCountByCollectionId,
    (state: {CurrentCollectionStore: CurrentCollectionStateType}) =>
      state.CurrentCollectionStore.currentCollection,
    (
      currentLocale,
      primaryLocale,
      localizedItemCountByLocaleId,
      itemCountByCollectionId,
      currentCollection
    ) => {
      let locale = currentLocale;

      // Special case: if we're currently in ecommerce then we have to
      // ensure we're using the primary locale to query for the item count,
      // as ecommerce items only exist in teh primary locale.
      if (currentCollection && isCommerceCollection(currentCollection)) {
        locale = primaryLocale;
      }

      if (currentLocale?.cmsId && localizedItemCountByLocaleId) {
        return (
          localizedItemCountByLocaleId.get(String(locale?.cmsId ?? '')) ||
          itemCountByCollectionId
        );
      }
      return itemCountByCollectionId;
    }
  );

export const selectTotalNumberOfItemsInSecondaries = createSelector(
  (state: {LocalizationStore: LocalizationState}) =>
    selectCurrentLocale(state.LocalizationStore),
  (state: {LocalizationStore: LocalizationState}) =>
    selectIsOnPrimaryLocale(state.LocalizationStore),
  (state: {LocalizationStore: LocalizationState}) =>
    selectPrimaryLocale(state.LocalizationStore),
  (state: {LocalizationStore: LocalizationState}) =>
    selectAllLocales(state.LocalizationStore),
  (state: {CurrentCollectionStore: CurrentCollectionStateType}) =>
    state.CurrentCollectionStore.currentCollection,
  (state: {CollectionStore: CollectionStateType}) =>
    state.CollectionStore.localizedItemCountByLocaleId,
  (
    currentLocale,
    isOnPrimaryLocale,
    primaryLocale,
    allLocales,
    currentCollection,
    localizedItemCountByLocaleId
  ): Array<{locale: Locale; count: number}> => {
    // Type check
    if (
      !currentCollection ||
      !currentLocale ||
      !primaryLocale ||
      !localizedItemCountByLocaleId
    ) {
      return emptyArray;
    }
    // Primary item propagation does not occur within ecommerce collections
    if (isCommerceCollection(currentCollection)) {
      return emptyArray;
    }
    // If we're not on the primary locale or if there aren't multiple locales on the site
    if (!isOnPrimaryLocale || allLocales.length <= 1) {
      return emptyArray;
    }

    return allLocales
      .filter((locale) => locale._id !== primaryLocale._id)
      .map((locale) => {
        if (!locale.cmsId) {
          return {locale, count: 0};
        }
        const itemCount = localizedItemCountByLocaleId.get(locale.cmsId);

        if (!itemCount) {
          return {locale, count: 0};
        }
        return {locale, count: getTotalNumberOfItems(itemCount)};
      })
      .filter((entry) => entry.count > 0);
  }
);

export function selectCreateComponentStatus(state: LocalizationState) {
  return state.createComponentState?.status ?? 'IDLE';
}

export function selectCreateComponentProgress(state: LocalizationState) {
  return state.createComponentState?.status === 'IN_PROGRESS'
    ? state.createComponentState.progress
    : null;
}

export function selectDuplicateComponentStatus(state: LocalizationState) {
  return state.duplicateComponentState?.status ?? 'IDLE';
}

export function selectDuplicateComponentProgress(state: LocalizationState) {
  return state.duplicateComponentState?.status === 'IN_PROGRESS'
    ? state.duplicateComponentState.progress
    : null;
}

export function selectUnlinkStatus(state: LocalizationState) {
  return state.unlinkState?.status ?? 'IDLE';
}

export function selectUnlinkProgress(state: LocalizationState) {
  return state.unlinkState?.status === 'IN_PROGRESS'
    ? state.unlinkState.progress
    : null;
}

export function selectCreateComponentPropertyStatus(state: LocalizationState) {
  return state.createComponentPropertyState?.status ?? 'IDLE';
}

export function selectCreateComponentPropertyProgress(
  state: LocalizationState
) {
  return state.createComponentPropertyState?.status === 'IN_PROGRESS'
    ? state.createComponentPropertyState.progress
    : null;
}

export function selectCorruptionRecoveryStatus(state: LocalizationState) {
  return state.corruptionRecoveryState?.status ?? 'IDLE';
}

export function selectCorruptionRecoveryProgress(state: LocalizationState) {
  return state.corruptionRecoveryState?.status === 'IN_PROGRESS'
    ? state.corruptionRecoveryState.progress
    : null;
}

export const selectIsAllowedToDraftPagesPerLocale = createSelector(
  (state: {EntitlementsStore: DesignerStores['EntitlementsStore']}) =>
    state.EntitlementsStore,
  (entitlementsStore) => {
    return (
      entitlementsStore.entitlements?.allowDraftPagesPerLocale?.hasAccess ??
      false
    );
  }
);

/**
 * Determines if a node (element, component instance, or symbol) is localized
 * based on its native ID path.
 *
 * A node is considered localized if it has been modified in a secondary locale.
 * This includes:
 * - Text content changes
 * - Style changes (CSS properties)
 * - Component property overrides
 * - Symbol/component instance localization
 */
export const selectIsNodeLocalizedByPath = (
  state: {
    LocalizationStore: LocalizationState;
    DesignerStore: DesignerStateType;
    UiNodeStore: UiNodeStateType;
    StyleBlockStore: DesignerStores['StyleBlockStore'];
  },
  nativeIdPath: NativeId[]
): boolean => {
  if (!nativeIdPath || nativeIdPath.length === 0) {
    return false;
  }

  const lastNativeId = nativeIdPath[nativeIdPath.length - 1];
  const localizedDomNodes = selectLocalizedDom(state.LocalizationStore);
  const localizedSymbolNodes = selectLocalizedSymbols(state.LocalizationStore);

  if (!lastNativeId) {
    return false;
  }

  // Check if the element exists in localized nodes first
  // This catches style changes, text changes, and other direct localizations
  if (localizedDomNodes[lastNativeId] || localizedSymbolNodes[lastNativeId]) {
    return true;
  }

  // Fallback to component map checks for complex scenarios
  const componentMap = selectComponents(state);
  const element = componentMap
    ? getElementFromNodeInInstanceNativeId([lastNativeId], componentMap)
    : null;

  if (!element) {
    return false;
  }

  const currentComponent = selectCurrentComponent(state);

  // Check for indirect localization (e.g., parent-child relationships)
  return (
    isElementLocalized(element, localizedDomNodes, currentComponent) ||
    isElementLocalized(element, localizedSymbolNodes, currentComponent) ||
    isElementStyleLocalized(element, state)
  );
};

export const selectIsLocaleChangeInProgress = (state: SharedStores) => {
  return state.UiStore.isPageLoading;
};

export function selectIsCorruptionRecoveryEnabled(
  state: LocalizationState
): boolean {
  return state.enableCorruptionRecovery ?? false;
}

export function selectIsLostTranslationsReportAvailable(
  state: LocalizationState
): boolean {
  return state.lostTranslationsReport?.available ?? false;
}

/**
 * True when the latest completed recovery run could not persist all of its
 * CSV report rows — the report (if downloadable at all) may be incomplete
 * and every download affordance must say so.
 */
export function selectIsLostTranslationsReportPartial(
  state: LocalizationState
): boolean {
  return state.lostTranslationsReport?.partial ?? false;
}

export function selectIsCorruptionRecoveryPanelOpen(
  state: LocalizationState
): boolean {
  return state.corruptionRecoveryPanel?.isOpen ?? false;
}

/**
 * New selector for LocaleSwitcher (feature flagged).
 * Unlike selectCanAddLocale, this works correctly when the panel is closed
 * by checking actual locales instead of staged locales.
 */
export const selectCanAddLocaleFromTopBar = createSelector(
  selectEffectiveMaxLocales,
  (state: DesignerStores) => state.LocalizationStore.staged,
  (state: DesignerStores) => selectAllLocales(state.LocalizationStore),
  (state: DesignerStores) => state.PanelsStore,
  (effectiveMaxLocales, staged, locales, PanelsStore) => {
    const LOCALIZATION_PANEL_NAME = 'localization';
    // Check if the Localization panel is active
    const isPanelActive = getIsPanelActive(
      PanelsStore,
      LOCALIZATION_PANEL_NAME
    );

    // Use staged locales when panel is open (they're being edited)
    // Use actual locales when panel is closed (staged might be stale)
    const currentNumberOfLocales = isPanelActive
      ? staged.locales.size - 1 // -1 to exclude primary
      : locales.length - 1; // -1 to exclude primary

    return currentNumberOfLocales < effectiveMaxLocales;
  }
);

export const selectLostTranslations = createSelector(
  (state: LocalizationState) => state.lostTranslations,
  (
    lostTranslations
  ): {
    records: LostTranslationRecord[];
    loading: boolean;
    error: boolean;
  } | null => {
    if (!lostTranslations) return null;
    return {
      records: lostTranslations.records,
      loading: lostTranslations.loading ?? false,
      error: lostTranslations.error ?? false,
    };
  }
);

export function selectElementSyncState(
  state: LocalizationState
): 'idle' | 'loading' | 'error' {
  return state.elementSyncState ?? 'idle';
}
